/**
 * applyTransforms — JS port of PHP `bim_cadaplus_apply_transforms`.
 *
 * Applies a sequential list of transforms to a string/number value.
 * Returns the transformed value, or `null` if a `filter_prefix` transform
 * matched (meaning the value should be excluded entirely).
 *
 * Transform types mirror the PHP implementation:
 *   trim, lowercase, uppercase, replace, regex, split, filter_prefix, to_int
 *
 * @param {string|number} value
 * @param {Array<{type: string, [key: string]: any}>} transforms
 * @returns {string|number|null}  null = filtered out
 */
export function applySourceTransforms(value, transforms) {
  if (!transforms || !Array.isArray(transforms)) return value

  let result = value

  for (const transform of transforms) {
    const type = transform.type ?? ''

    switch (type) {
      case 'trim':
        result = String(result).trim()
        break

      case 'lowercase':
        result = String(result).toLowerCase()
        break

      case 'uppercase':
        result = String(result).toUpperCase()
        break

      case 'replace': {
        // PHP str_replace replaces all occurrences
        const pattern     = transform.pattern ?? ''
        const replacement = transform.replacement ?? ''
        result = String(result).split(pattern).join(replacement)
        break
      }

      case 'regex': {
        const pattern     = transform.pattern ?? ''
        const replacement = transform.replacement ?? ''
        if (pattern) {
          result = String(result).replace(new RegExp(pattern, 'g'), replacement)
        }
        break
      }

      case 'split': {
        const separator = transform.separator ?? ''
        const take      = transform.take ?? 0
        if (separator) {
          const parts = String(result).split(separator)
          const idx   = take < 0 ? parts.length + take : take
          result      = parts[idx] ?? result
        }
        break
      }

      case 'filter_prefix': {
        // Return null to exclude values starting with any of the listed prefixes
        const exclude = transform.exclude ?? []
        if (Array.isArray(exclude)) {
          for (const prefix of exclude) {
            if (String(result).startsWith(String(prefix))) return null
          }
        }
        break
      }

      case 'to_int':
        result = parseInt(String(result), 10)
        break

      default:
        console.warn(`[applyTransforms] Unknown transform type: "${type}"`)
        break
    }
  }

  return result
}
